import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { researchService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

const ResearchEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      title: '',
      venue: '',
      type: 'Conference Paper',
      description: '',
      status: 'In Progress',
      date: '',
      authors: '',
      link: ''
    }
  });

  useEffect(() => {
    if (isEditing) {
      loadResearch();
    }
  }, [id]);

  const loadResearch = async () => {
    setInitialLoading(true);
    try {
      const result = await researchService.getById(id);
      if (result.success) {
        reset(result.data);
      } else {
        toast.error('Research not found');
        navigate('/adminaccess/research');
      }
    } catch (error) {
      toast.error('Error loading research');
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      let result;
      if (isEditing) {
        result = await researchService.update(id, data);
      } else {
        result = await researchService.create(data);
      }

      if (result.success) {
        toast.success(isEditing ? 'Research updated!' : 'Research added!');
        navigate('/adminaccess/research');
      } else {
        toast.error('Failed to save research');
      }
    } catch (error) {
      toast.error('Error saving research');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading research...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/research')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <h1 className="text-lg font-medium">
            {isEditing ? 'Edit Research' : 'New Research'}
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          
          {/* Title */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Title *
            </label>
            <input
              type="text"
              {...register('title', { required: 'Title is required' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="Quantum Computing Applications in Machine Learning"
            />
            {errors.title && (
              <p className="text-red-400 text-xs mt-1">{errors.title.message}</p>
            )}
          </div>

          {/* Venue */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Venue *
            </label>
            <input
              type="text"
              {...register('venue', { required: 'Venue is required' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="IEEE Quantum Computing Conference 2024"
            />
            {errors.venue && (
              <p className="text-red-400 text-xs mt-1">{errors.venue.message}</p>
            )}
          </div>

          {/* Type - FIXED */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Type *
            </label>
            <select
              {...register('type')}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer
              [&>option]:bg-[#1a1a1a] [&>option]:text-white [&>option]:py-2"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' fill-opacity='0.6' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                paddingRight: '2.5rem'
              }}
            >
              <option value="Conference Paper">Conference Paper</option>
              <option value="Journal Article">Journal Article</option>
              <option value="Workshop Paper">Workshop Paper</option>
              <option value="Survey Paper">Survey Paper</option>
              <option value="Thesis">Thesis</option>
              <option value="Ongoing Research">Ongoing Research</option>
              <option value="Technical Report">Technical Report</option>
            </select>
          </div>

          {/* Description - NOW OPTIONAL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={5}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
              placeholder="Brief description of your research (optional)..."
            />
            <p className="text-white/30 text-xs mt-1">
              Optional: Add a description for your research
            </p>
          </div>

          {/* Authors */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Authors *
            </label>
            <input
              type="text"
              {...register('authors', { required: 'Authors is required' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="Aakash Poudel, Dr. Jane Smith, Dr. Robert Johnson"
            />
            {errors.authors && (
              <p className="text-red-400 text-xs mt-1">{errors.authors.message}</p>
            )}
            <p className="text-white/30 text-xs mt-1">Comma-separated list</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Status - FIXED */}
            <div>
              <label className="block text-white/60 text-sm mb-2">
                Status *
              </label>
              <select
                {...register('status')}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white/30 transition-colors appearance-none cursor-pointer
                [&>option]:bg-[#1a1a1a] [&>option]:text-white [&>option]:py-2"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' fill-opacity='0.6' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="Published">Published</option>
                <option value="Under Review">Under Review</option>
                <option value="In Progress">In Progress</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            {/* Date */}
            <div>
              <label className="block text-white/60 text-sm mb-2">
                Date *
              </label>
              <input
                type="text"
                {...register('date', { required: 'Date is required' })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="March 2024"
              />
              {errors.date && (
                <p className="text-red-400 text-xs mt-1">{errors.date.message}</p>
              )}
              <p className="text-white/30 text-xs mt-1">e.g. "March 2024" or "Expected: June 2025"</p>
            </div>
          </div>

          {/* Link */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Publication Link
            </label>
            <input
              type="text"
              {...register('link')}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="https://ieeexplore.ieee.org/document/..."
            />
            <p className="text-white/30 text-xs mt-1">Full URL to publication (optional, use # if not available)</p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : isEditing ? 'Update Research' : 'Add Research'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/adminaccess/research')}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ResearchEditor;